import { Static, Type } from '@sinclair/typebox'
import { Nullable } from '../../common'
import { ApId } from '../../common/id-generator'
import { BranchCondition, CodeActionSchema, LoopOnItemsActionSchema, PieceActionSchema, RouterActionSchema } from '../actions/action'
import { Connection } from '../connections/connection'
import { FlowStatus } from '../flow'
import { FlowVersion, FlowVersionState } from '../flow-version'
import { EmptyTrigger, PieceTrigger, Trigger } from '../triggers/trigger'
import { flowPieceUtil } from '../util/flow-piece-util'
import { flowStructureUtil } from '../util/flow-structure-util'
import { _addAction } from './add-action'
import { _addBranch } from './add-branch'
import { _attachSteps } from './attach-steps'
import { _getActionsForCopy } from './copy-action-operations'
import { _deleteAction } from './delete-action'
import { _deleteBranch } from './delete-branch'
import { _detachSteps } from './detach-steps'
import { _dragStep } from './drag-step'
import { _duplicateBranch, _duplicateStep } from './duplicate-step'
import { _importFlow } from './import-flow'
import { flowMigrations } from './migrations'
import { _moveAction } from './move-action'
import { _getOperationsForPaste } from './paste-operations'
import { _skipAction } from './skip-action'
import { _updateAction } from './update-action'
import { _updateTrigger } from './update-trigger'
import {
    AI_BUILDER_CONTEXT_HARD_CAP,
    AiBuilderAbout,
    AiBuilderMessage,
    AiBuilderTurnInput,
    DynamicPropertiesValues,
} from '../../ai-builder'


export enum FlowOperationType {
    LOCK_AND_PUBLISH = 'LOCK_AND_PUBLISH',
    CHANGE_STATUS = 'CHANGE_STATUS',
    LOCK_FLOW = 'LOCK_FLOW',
    CHANGE_FOLDER = 'CHANGE_FOLDER',
    CHANGE_NAME = 'CHANGE_NAME',
    MOVE_ACTION = 'MOVE_ACTION',
    IMPORT_FLOW = 'IMPORT_FLOW',
    UPDATE_TRIGGER = 'UPDATE_TRIGGER',
    ADD_ACTION = 'ADD_ACTION',
    UPDATE_ACTION = 'UPDATE_ACTION',
    DELETE_ACTION = 'DELETE_ACTION',
    DUPLICATE_ACTION = 'DUPLICATE_ACTION',
    USE_AS_DRAFT = 'USE_AS_DRAFT',
    DELETE_BRANCH = 'DELETE_BRANCH',
    ADD_BRANCH = 'ADD_BRANCH',
    DUPLICATE_BRANCH = 'DUPLICATE_BRANCH',
    SET_SKIP_ACTION = 'SET_SKIP_ACTION',
    DRAG_STEP = 'DRAG_STEP',
    DETACH_STEPS = 'DETACH_STEPS',
    ATTACH_STEPS = 'ATTACH_STEPS',
    GENERATE_FLOW = 'GENERATE_FLOW',
    GENERATE_PROP = 'GENERATE_PROP',
    UPDATE_DYNAMIC_PROPERTIES_VALUES = 'UPDATE_DYNAMIC_PROPERTIES_VALUES',
    REPLACE_VERSION = 'REPLACE_VERSION',
}

export const DeleteBranchRequest = Type.Object({
    branchIndex: Type.Number(),
    stepName: Type.String(),
})
export const AddBranchRequest = Type.Object({
    branchIndex: Type.Number(),
    stepName: Type.String(),
    conditions: Type.Optional(Type.Array(Type.Array(BranchCondition))),
    branchName: Type.String(),
})

export const SkipActionRequest = Type.Object({
    names: Type.Array(Type.String()),
    skip: Type.Boolean(),
})

export type SkipActionRequest = Static<typeof SkipActionRequest>

export const DuplicateBranchRequest = Type.Object({
    branchIndex: Type.Number(),
    stepName: Type.String(),
})
export type DeleteBranchRequest = Static<typeof DeleteBranchRequest>
export type AddBranchRequest = Static<typeof AddBranchRequest>
export type DuplicateBranchRequest = Static<typeof DuplicateBranchRequest>

export enum StepLocationRelativeToParent {
    AFTER = 'AFTER',
    INSIDE_LOOP = 'INSIDE_LOOP',
    INSIDE_BRANCH = 'INSIDE_BRANCH',
}

export const UseAsDraftRequest = Type.Object({
    versionId: Type.String(),
})
export type UseAsDraftRequest = Static<typeof UseAsDraftRequest>

export const LockFlowRequest = Type.Object({})

export type LockFlowRequest = Static<typeof LockFlowRequest>

export const ImportFlowRequest = Type.Object({
    displayName: Type.String({}),
    triggers: Type.Array(Trigger),
    actions: Type.Array(Type.Any()),
    connections: Type.Array(Connection),
    schemaVersion: Nullable(Type.String()),
    dynamicPropertiesValues: Type.Optional(DynamicPropertiesValues),
})

export type ImportFlowRequest = Static<typeof ImportFlowRequest>

export const ChangeFolderRequest = Type.Object({
    folderId: Nullable(Type.String({})),
})

export type ChangeFolderRequest = Static<typeof ChangeFolderRequest>

export const ChangeNameRequest = Type.Object({
    displayName: Type.String({}),
})

export type ChangeNameRequest = Static<typeof ChangeNameRequest>


export const DeleteActionRequest = Type.Object({
    names: Type.Array(Type.String()),
})

export type DeleteActionRequest = Static<typeof DeleteActionRequest>

export const UpdateActionRequest = Type.Union([
    CodeActionSchema,
    LoopOnItemsActionSchema,
    PieceActionSchema,
    RouterActionSchema,
])

export type UpdateActionRequest = Static<typeof UpdateActionRequest>

export const DuplicateStepRequest = Type.Object({
    stepName: Type.String(),
})

export type DuplicateStepRequest = Static<typeof DuplicateStepRequest>

export const MoveActionRequest = Type.Object({
    name: Type.String(),
    newParentStep: Type.String(),
    stepLocationRelativeToNewParent: Type.Optional(
        Type.Enum(StepLocationRelativeToParent),
    ),
    branchIndex: Type.Optional(Type.Number()),
    newStepPosition: Type.Optional(Type.Object({ x: Type.Number(), y: Type.Number() })),
    skipImportOperations: Type.Optional(Type.Boolean()),
})
export type MoveActionRequest = Static<typeof MoveActionRequest>

export const AddActionRequest = Type.Object({
    parentStep: Type.Array(Type.String()),
    stepLocationRelativeToParent: Type.Optional(
        Type.Enum(StepLocationRelativeToParent),
    ),
    branchIndex: Type.Optional(Type.Number()),
    action: UpdateActionRequest,
    connection: Type.Optional(Connection),
    newStepPosition: Type.Optional(Type.Object({ x: Type.Number(), y: Type.Number() })),
})
export type AddActionRequest = Static<typeof AddActionRequest>

export const UpdateTriggerRequest = Type.Union([EmptyTrigger, PieceTrigger])
export type UpdateTriggerRequest = Static<typeof UpdateTriggerRequest>

export const UpdateFlowStatusRequest = Type.Object({
    status: Type.Enum(FlowStatus),
})
export type UpdateFlowStatusRequest = Static<typeof UpdateFlowStatusRequest>

export const ChangePublishedVersionIdRequest = Type.Object({})
export type ChangePublishedVersionIdRequest = Static<
    typeof ChangePublishedVersionIdRequest
>

export const DragStepRequest = Type.Object({
    stepName: Type.String(),
    newPosition: Type.Object({
        x: Type.Number(),
        y: Type.Number(),
    }),
})

export type DragStepRequest = Static<typeof DragStepRequest>

export const DetachStepsRequest = Type.Object({
    connection: Connection,
})

export type DetachStepsRequest = Static<typeof DetachStepsRequest>

export const AttachStepsRequest = Type.Object({
    connection: Connection,
})

export type AttachStepsRequest = Static<typeof AttachStepsRequest>

/**
 * What a turn's `replyTo` field carries — exported so the front's
 * `AiBuilderChatMessage.sourceReplyTo` shares this one shape instead of a
 * second declaration that could drift.
 */
export const ReplyTo = Type.Union([Type.Object({ questionId: ApId }), Type.Literal('none')])
export type ReplyTo = Static<typeof ReplyTo>

export const GenerateFlowRequest = Type.Object({
    flowId: Type.String({}),
    /** A button, a card answer, a help request, a resume or free text — typed at the source, never a bracketed prompt string */
    input: AiBuilderTurnInput,
    context: Type.Array(AiBuilderMessage, {
        maxItems: AI_BUILDER_CONTEXT_HARD_CAP,
    }),
    /** Correlates the GENERATE_FLOW_FINISHED response with its request on a shared socket */
    requestId: Type.Optional(Type.String({})),
    /**
     * Whether the flow still carries its creation name. The front creates it
     * TRANSLATED ("Sans titre"), so only the client can tell a default name
     * from one the user chose — and a name the user chose is never overwritten.
     */
    flowIsUnnamed: Type.Optional(Type.Boolean()),
    /**
     * What the pending-question reply chip was doing when this message was
     * sent: `{questionId}` — the chip was shown and left there; `'none'` —
     * the user dismissed it; absent — a silent action or retry, or nothing
     * to answer. `pendingQuestionViewOf` reads absent like a matching
     * `{questionId}`: a shown chip the client said nothing about is not
     * evidence nothing was pending.
     */
    replyTo: Type.Optional(ReplyTo),
    /**
     * What this conversation was opened about: a specific run or step, from
     * the "Understand this error" entry point. Absent for an ordinary message.
     */
    about: Type.Optional(AiBuilderAbout),
    /**
     * The front's own id for this conversation: stable across
     * turns, renewed only by "New conversation". A value that differs from
     * the session thread's own starts a fresh thread server-side. Absent
     * (an old front) keeps today's behaviour — the thread is never reset.
     */
    conversationId: Type.Optional(Type.String({ maxLength: 100 })),
})

export type GenerateFlowRequest = Static<typeof GenerateFlowRequest>

export const GeneratePropRequest = Type.Object({
    flowId: Type.String({}),
    prompt: Type.String({}),
    context: Type.Array(AiBuilderMessage),
    stepName: Type.String(),
    dynamicPropertiesValues: Type.Optional(Type.Record(Type.String(), Type.Any())),
})

export type GeneratePropRequest = Static<typeof GeneratePropRequest>

export const UpdateDynamicPropertiesValuesRequest = Type.Object({
    dynamicPropertiesValues: DynamicPropertiesValues,
});
export type UpdateDynamicPropertiesValuesRequest = Static<typeof UpdateDynamicPropertiesValuesRequest>;

export const ReplaceVersionRequest = Type.Object({
    flowVersion: FlowVersion,
});
export type ReplaceVersionRequest = Static<typeof ReplaceVersionRequest>;


export const FlowOperationRequest = Type.Union([
    Type.Object(
        {
            type: Type.Literal(FlowOperationType.MOVE_ACTION),
            request: MoveActionRequest,
        },
        {
            title: 'Move Action',
        },
    ),
    Type.Object(
        {
            type: Type.Literal(FlowOperationType.CHANGE_STATUS),
            request: UpdateFlowStatusRequest,
        },
        {
            title: 'Change Status',
        },
    ),
    Type.Object(
        {
            type: Type.Literal(FlowOperationType.LOCK_AND_PUBLISH),
            request: ChangePublishedVersionIdRequest,
        },
        {
            title: 'Lock and Publish',
        },
    ),
    Type.Object(
        {
            type: Type.Literal(FlowOperationType.USE_AS_DRAFT),
            request: UseAsDraftRequest,
        },
        {
            title: 'Copy as Draft',
        },
    ),
    Type.Object(
        {
            type: Type.Literal(FlowOperationType.LOCK_FLOW),
            request: LockFlowRequest,
        },
        {
            title: 'Lock Flow',
        },
    ),
    Type.Object(
        {
            type: Type.Literal(FlowOperationType.IMPORT_FLOW),
            request: ImportFlowRequest,
        },
        {
            title: 'Import Flow',
        },
    ),
    Type.Object(
        {
            type: Type.Literal(FlowOperationType.CHANGE_NAME),
            request: ChangeNameRequest,
        },
        {
            title: 'Change Name',
        },
    ),
    Type.Object(
        {
            type: Type.Literal(FlowOperationType.DELETE_ACTION),
            request: DeleteActionRequest,
        },
        {
            title: 'Delete Action',
        },
    ),
    Type.Object(
        {
            type: Type.Literal(FlowOperationType.UPDATE_ACTION),
            request: UpdateActionRequest,
        },
        {
            title: 'Update Action',
        },
    ),
    Type.Object(
        {
            type: Type.Literal(FlowOperationType.ADD_ACTION),
            request: AddActionRequest,
        },
        {
            title: 'Add Action',
        },
    ),
    Type.Object(
        {
            type: Type.Literal(FlowOperationType.UPDATE_TRIGGER),
            request: UpdateTriggerRequest,
        },
        {
            title: 'Update Trigger',
        },
    ),
    Type.Object(
        {
            type: Type.Literal(FlowOperationType.CHANGE_FOLDER),
            request: ChangeFolderRequest,
        },
        {
            title: 'Change Folder',
        },
    ),
    Type.Object(
        {
            type: Type.Literal(FlowOperationType.DUPLICATE_ACTION),
            request: DuplicateStepRequest,
        },
        {
            title: 'Duplicate Action',
        },
    ),
    Type.Object(
        {
            type: Type.Literal(FlowOperationType.DELETE_BRANCH),
            request: DeleteBranchRequest,
        },
        {
            title: 'Delete Branch',
        },
    ),
    Type.Object(
        {
            type: Type.Literal(FlowOperationType.ADD_BRANCH),
            request: AddBranchRequest,
        },
        {
            title: 'Add Branch',
        },
    ),
    Type.Object(
        {
            type: Type.Literal(FlowOperationType.DUPLICATE_BRANCH),
            request: DuplicateBranchRequest,
        },
        {
            title: 'Duplicate Branch',
        },
    ),
    Type.Object(
        {
            type: Type.Literal(FlowOperationType.SET_SKIP_ACTION),
            request: SkipActionRequest,
        },
        {
            title: 'Skip Action',
        },
    ),
    Type.Object(
        {
            type: Type.Literal(FlowOperationType.DRAG_STEP),
            request: DragStepRequest,
        },
        {
            title: 'Drag Step',
        },
    ),
    Type.Object(
        {
            type: Type.Literal(FlowOperationType.DETACH_STEPS),
            request: DetachStepsRequest,
        },
        {
            title: 'Detach Steps',
        },
    ),
    Type.Object(
        {
            type: Type.Literal(FlowOperationType.ATTACH_STEPS),
            request: AttachStepsRequest,
        },
        {
            title: 'Attach Steps',
        },
    ),
    Type.Object(
        {
            type: Type.Literal(FlowOperationType.GENERATE_FLOW),
            request: GenerateFlowRequest,
        },
        {
            title: 'Generate Flow',
        },
    ),
    Type.Object(
        {
            type: Type.Literal(FlowOperationType.GENERATE_PROP),
            request: GeneratePropRequest,
        },
        {
            title: 'Generate Prop',
        },
    ),
    Type.Object(
        {
            type: Type.Literal(FlowOperationType.UPDATE_DYNAMIC_PROPERTIES_VALUES),
            request: UpdateDynamicPropertiesValuesRequest,
        },
        {
            title: 'Update Dynamic Properties Values',
        },
    ),
    Type.Object(
        {
            type: Type.Literal(FlowOperationType.REPLACE_VERSION),
            request: ReplaceVersionRequest,
        },
        {
            title: 'Replace Version',
        },
    ),
])

export type FlowOperationRequest = Static<typeof FlowOperationRequest>

export const flowOperations = {
    getActionsForCopy: _getActionsForCopy,
    getOperationsForPaste: _getOperationsForPaste,
    apply(flowVersion: FlowVersion, operation: FlowOperationRequest): FlowVersion {
        let clonedVersion: FlowVersion = JSON.parse(JSON.stringify(flowVersion))
        switch (operation.type) {
            case FlowOperationType.MOVE_ACTION: {
                const operations: FlowOperationRequest[] = _moveAction(clonedVersion, operation.request)
                operations.forEach((operation) => {
                    clonedVersion = flowOperations.apply(clonedVersion, operation)
                })
                clonedVersion = flowPieceUtil.makeFlowAutoUpgradable(clonedVersion)
                break
            }
            case FlowOperationType.CHANGE_NAME:
                clonedVersion.displayName = operation.request.displayName
                break
            case FlowOperationType.DUPLICATE_BRANCH: {
                const operations = _duplicateBranch(operation.request.stepName, operation.request.branchIndex, clonedVersion)
                operations.forEach((operation) => {
                    clonedVersion = flowOperations.apply(clonedVersion, operation)
                })
                break
            }
            case FlowOperationType.DUPLICATE_ACTION: {
                const operations = _duplicateStep(operation.request.stepName, clonedVersion)
                operations.forEach((operation) => {
                    clonedVersion = flowOperations.apply(clonedVersion, operation)
                })
                break
            }
            case FlowOperationType.LOCK_FLOW:
                clonedVersion.state = FlowVersionState.LOCKED
                break
            case FlowOperationType.ADD_ACTION: {
                clonedVersion = _addAction(clonedVersion, operation.request)
                clonedVersion = flowPieceUtil.makeFlowAutoUpgradable(clonedVersion)
                break
            }
            case FlowOperationType.DELETE_ACTION: {
                clonedVersion = _deleteAction(clonedVersion, operation.request)
                clonedVersion = flowPieceUtil.makeFlowAutoUpgradable(clonedVersion)
                break
            }
            case FlowOperationType.UPDATE_TRIGGER: {
                clonedVersion = _updateTrigger(clonedVersion, operation.request)
                clonedVersion = flowPieceUtil.makeFlowAutoUpgradable(clonedVersion)
                break
            }
            case FlowOperationType.ADD_BRANCH: {
                clonedVersion = _addBranch(clonedVersion, operation.request)
                clonedVersion = flowPieceUtil.makeFlowAutoUpgradable(clonedVersion)
                break
            }
            case FlowOperationType.DELETE_BRANCH: {
                clonedVersion = _deleteBranch(clonedVersion, operation.request)
                clonedVersion = flowPieceUtil.makeFlowAutoUpgradable(clonedVersion)
                break
            }
            case FlowOperationType.UPDATE_ACTION: {
                clonedVersion = _updateAction(clonedVersion, operation.request)
                clonedVersion = flowPieceUtil.makeFlowAutoUpgradable(clonedVersion)
                break
            }
            case FlowOperationType.IMPORT_FLOW: {
                const migratedFlow = flowMigrations.apply({
                    ...clonedVersion,
                    displayName: operation.request.displayName,
                    schemaVersion: operation.request.schemaVersion,
                    triggers: operation.request.triggers,
                    actions: operation.request.actions,
                    connections: operation.request.connections,
                })

                const operations = _importFlow(clonedVersion, {
                    displayName: migratedFlow.displayName,
                    triggers: migratedFlow.triggers,
                    actions: migratedFlow.actions,
                    connections: migratedFlow.connections,
                    schemaVersion: migratedFlow.schemaVersion,
                })
                operations.forEach((operation) => {
                    clonedVersion = flowOperations.apply(clonedVersion, operation)
                })
                if (operation.request.dynamicPropertiesValues) {
                    clonedVersion.dynamicPropertiesValues = {
                        ...clonedVersion.dynamicPropertiesValues,
                        ...operation.request.dynamicPropertiesValues,
                    }
                }
                break
            }
            case FlowOperationType.SET_SKIP_ACTION: {
                clonedVersion = _skipAction(clonedVersion, operation.request)
                clonedVersion = flowPieceUtil.makeFlowAutoUpgradable(clonedVersion)
                break
            }
            case FlowOperationType.DRAG_STEP: {
                _dragStep(clonedVersion, operation.request)
                break
            }
            case FlowOperationType.DETACH_STEPS: {
                _detachSteps(clonedVersion, operation.request)
                break
            }
            case FlowOperationType.ATTACH_STEPS: {
                clonedVersion = _attachSteps(clonedVersion, operation.request)
                clonedVersion = flowPieceUtil.makeFlowAutoUpgradable(clonedVersion)
                break
            }
            case FlowOperationType.UPDATE_DYNAMIC_PROPERTIES_VALUES: {
                clonedVersion.dynamicPropertiesValues = {
                    ...clonedVersion.dynamicPropertiesValues,
                    ...operation.request.dynamicPropertiesValues,
                  };
                break
            }
            case FlowOperationType.REPLACE_VERSION: {
                clonedVersion = JSON.parse(JSON.stringify(operation.request.flowVersion));
                break
            }
            default:
                break
        }
        clonedVersion.valid = flowStructureUtil.getAllSteps(clonedVersion).every((step) => step.valid)
        return clonedVersion
    },
}
